import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Fab,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkList from '@repo/shared/components/LinkList';
import HelpText from '@repo/shared/components/HelpText';
import CreateMeetingSidePane from '@repo/shared/components/CreateMeetingSidePane';
import CreateMeetingDialog from '@repo/shared/components/CreateMeetingDialog';
import { usePolyAuth } from '@repo/shared/hooks/PolyAuthContext';
import useFastOpenMeeting from '@repo/shared/hooks/useFastOpenMeeting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { endpoint, getLinkPrefix } from '../utils/utils';
import { useEmailInvite } from '../utils/emailInvite';
import { toast } from 'sonner';
import { mutate } from 'swr';
import zoomSdk from '@zoom/appssdk';

export default function Meetings() {
    const { t } = useTranslation('shared');
    const { zoomAccountId, accountId } = usePolyAuth();
    const flags = useFlags();
    const linkPrefix = getLinkPrefix(flags);

    const swrKey = accountId
        ? `${linkPrefix}/listlinks/${encodeURIComponent(zoomAccountId)}?account=${accountId}`
        : `${linkPrefix}/listlinks/${encodeURIComponent(zoomAccountId)}`;

    const [panelOpen, setPanelOpen] = useState(false);
    // Edit and duplicate open the dialog; the side pane stays the create surface.
    const [dialogLink, setDialogLink] = useState(null);
    const [editing, setEditing] = useState(false);
    const hasAutoOpened = useRef(false);

    const { openEmailInvite } = useEmailInvite((error) => {
        toast.error(t('meetings.failedEmailInvite', { error: error.message }));
    });

    const { openMeeting } = useFastOpenMeeting({
        onLaunchMeeting: async (joinUrl) => {
            await zoomSdk.launchAppInMeeting({ joinURL: joinUrl });
        },
        onFallbackVerify: async (link) => {
            await zoomSdk.openUrl({ url: link.url || `https://${endpoint()}${linkPrefix}/${link.link_uuid}` });
            toast.info(t('meetings.openingInBrowser'));
        },
    });

    const handleSendInvite = useCallback(async (link) => {
        try {
            await openEmailInvite({
                title: link.title || t('callLogs.untitled'),
                url: link.url,
                type: 'meeting'
            });
        } catch (error) {
            console.error('Error opening email invite:', error);
            toast.error(t('meetings.failedEmailInviteGeneric'));
        }
    }, [openEmailInvite]);

    const openEdit = useCallback((link) => {
        setDialogLink(link);
        setEditing(true);
    }, []);

    const openDuplicate = useCallback((link) => {
        setDialogLink({ ...link, title: '' });
        setEditing(false);
    }, []);

    const handleLinkCountChange = useCallback((count) => {
        if (!hasAutoOpened.current && count < 2) {
            setPanelOpen(true);
            hasAutoOpened.current = true;
        }
    }, []);

    const setFeedback = ({type, message}) => {
        if (type === "success") {
            toast.success(message);
        } else if (type === "error") {
            toast.error(message);
        } else {
            toast(message);
        }
    };

    return (
        <Box sx={{ pt: 1, display: 'flex', gap: 2, flex: 1, minHeight: 0, position: 'relative' }}>
            <Fab
                color="primary"
                size="medium"
                onClick={() => setPanelOpen(!panelOpen)}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 16,
                    zIndex: 2,
                    transition: 'transform 0.3s ease',
                    transform: panelOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                }}
            >
                <AddIcon />
            </Fab>
            <Box sx={{ flex: 1, minWidth: { xs: 0, md: 480 }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 500,
                        }}
                    >
                        {t('meetings.title')}
                    </Typography>
                </Box>

                <HelpText>
                    {t('meetings.helpText')}
                </HelpText>

                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <LinkList
                        listEndpoint={swrKey}
                        onOpenMeeting={openMeeting}
                        onSendInvite={handleSendInvite}
                        onEditLink={openEdit}
                        onDuplicateLink={openDuplicate}
                        onLinkCountChange={handleLinkCountChange}
                        enablePagination
                        enableSearch
                    />
                </Box>
            </Box>
            <CreateMeetingSidePane
                open={panelOpen}
                onClose={(event, meetingURL) => {
                    setPanelOpen(false);
                    if (meetingURL) {
                        mutate(key => typeof key === 'string' && key.startsWith(swrKey));
                    }
                }}
                accountId={zoomAccountId}
                listCacheKey={swrKey}
                setFeedback={setFeedback}
                linkType="zoom"
                hideCloseButton
            />
            <CreateMeetingDialog
                open={!!dialogLink}
                onClose={(event, meetingURL) => {
                    setDialogLink(null);
                    if (meetingURL) {
                        mutate(key => typeof key === 'string' && key.startsWith(swrKey));
                    }
                }}
                accountId={zoomAccountId}
                listCacheKey={swrKey}
                setFeedback={setFeedback}
                linkType="zoom"
                initialLinkData={dialogLink}
                linkUuid={editing ? dialogLink?.link_uuid : undefined}
                isEditMode={editing}
            />
        </Box>
    );
}
